import { DocumentIcon } from '@heroicons/react/24/outline';

import { Spinner } from '@/components/ui/spinner';
import { useMy } from '@/hooks/useMy';
import { cn } from '@/utils/classname';

interface SharedFilePickerProps {
  query: string;
  acceptedExtensions?: string[];
  onSelect: (filePath: string, fileName: string) => void;
}

export function SharedFilePicker({
  query,
  acceptedExtensions,
  onSelect,
}: SharedFilePickerProps) {
  const { data: files = [], status } = useMy({
    resource: 'file',
    query,
  });

  const filtered = acceptedExtensions
    ? files.filter(f =>
        acceptedExtensions.some(ext => f.name.toLowerCase().endsWith(ext))
      )
    : files;

  if (status === 'pending') {
    return (
      <div className="flex items-center justify-center p-6">
        <Spinner size="sm" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="bg-chariot-background-secondary rounded-lg p-6 text-center">
        <p className="text-chariot-text-secondary text-sm">
          Failed to load shared files. Please try again.
        </p>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="bg-chariot-background-secondary rounded-lg p-6 text-center">
        <p className="text-chariot-text-secondary text-sm">
          No matching files found in shared files
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-64 space-y-1 overflow-y-auto">
      {filtered.map(file => (
        <button
          key={file.key}
          type="button"
          className={cn(
            'flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors',
            'hover:bg-chariot-background-secondary border border-transparent',
            'hover:border-chariot-stroke'
          )}
          onClick={() =>
            onSelect(file.name, file.name.split('/').pop() ?? file.name)
          }
        >
          <DocumentIcon className="text-chariot-brand size-6 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-chariot-text-primary truncate text-sm font-medium">
              {file.name.split('/').pop()}
            </p>
            <p className="text-chariot-text-tertiary text-xs">{file.name}</p>
          </div>
        </button>
      ))}
    </div>
  );
}
